import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { ShieldCheck, Smartphone, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { api, errMsg } from '../api';
import { usePortal } from '../PortalAuth';
import { PageHeader, Card, SectionTitle, inputCls, goldBtn, ghostBtn } from '../ui';
import CoverPreference from '../report/CoverPreference';
import DesignPreference from '../report/DesignPreference';
import GraphStylePreference from '../report/GraphStylePreference';
import TravelAlertPreference from '../travel/TravelAlertPreference';
import MonitoringEmailPref from '../monitoring/MonitoringEmailPref';
import { canSee } from '../shells/nav';
import PushCard from '../components/PushCard';
import PasskeyList from '../PasskeyList';

export default function SecurityPage() {
  const { user, refresh } = usePortal();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const start = async () => {
    try { const { data } = await api.post('/auth/totp/setup'); setSetup(data); setCode(''); } catch (e) { toast.error(errMsg(e)); }
  };
  const act = async (path, msg) => {
    setBusy(true);
    try { await api.post(path, { code }); toast.success(msg); setSetup(null); setCode(''); await refresh(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader kicker="Account" title="Impostazioni e sicurezza" />
      <PushCard />
      <div className="h-4" />
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-8">
          <SectionTitle>Verifica in due passaggi</SectionTitle>
          <div className="space-y-4 mb-8">
            <div className="flex items-center gap-4"><Mail className="text-[#D4AF37]" size={20} /><div className="flex-1"><p className="text-sm">Codice via email</p><p className="text-xs text-[#5A5F67]">Sempre attivo come metodo di riserva</p></div><span className="text-xs text-[#5FC18F]">Attivo</span></div>
            <PasskeyList client={api} base="/auth/passkeys" who={user.email} />
            <div className="flex items-center gap-4"><Smartphone className="text-[#D4AF37]" size={20} /><div className="flex-1"><p className="text-sm">App di autenticazione</p><p className="text-xs text-[#5A5F67]">Google Authenticator, Microsoft Authenticator, Authy…</p></div><span className={`text-xs ${user.totp_enabled ? 'text-[#5FC18F]' : 'text-[#7A7F87]'}`} data-testid="totp-status">{user.totp_enabled ? 'Attiva' : 'Non attiva'}</span></div>
          </div>
          {!user.totp_enabled && !setup && <Button onClick={start} className={`${goldBtn} h-11`} data-testid="totp-start"><ShieldCheck size={16} className="mr-2" />Attiva app di autenticazione</Button>}
          {user.totp_enabled && (
            <div className="flex gap-3">
              <Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} placeholder="Codice attuale" className={`${inputCls} font-mono`} data-testid="totp-disable-code" />
              <Button onClick={() => act('/auth/totp/disable', 'App di autenticazione disattivata')} disabled={busy || code.length !== 6} className={`${ghostBtn} h-11`} data-testid="totp-disable">Disattiva</Button>
            </div>
          )}
        </Card>
        {setup && (
          <Card className="p-8 portal-rise" data-testid="totp-setup-card">
            <SectionTitle>Configura l'app</SectionTitle>
            <ol className="text-sm text-[#A3A3A3] space-y-2 mb-6 list-decimal pl-5">
              <li>Apri l'app di autenticazione e scegli "Aggiungi account".</li>
              <li>Inquadra il codice QR oppure inserisci la chiave manualmente.</li>
              <li>Scrivi qui sotto il codice a 6 cifre che compare.</li>
            </ol>
            <div className="bg-white p-4 w-fit mb-4"><QRCodeSVG value={setup.otpauth_uri} size={168} /></div>
            <p className="text-xs font-mono text-[#8A8F97] break-all mb-6" data-testid="totp-secret">{setup.secret}</p>
            <div className="flex gap-3">
              <Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} placeholder="123456" className={`${inputCls} font-mono`} data-testid="totp-enable-code" />
              <Button onClick={() => act('/auth/totp/enable', 'App di autenticazione attivata')} disabled={busy || code.length !== 6} className={`${goldBtn} h-11`} data-testid="totp-enable">{busy ? <Loader2 className="animate-spin" size={16} /> : 'Conferma'}</Button>
            </div>
          </Card>
        )}
      </div>
      <DesignPreference />
      <GraphStylePreference />
      <CoverPreference />
      {user.travel_enabled && <TravelAlertPreference />}
      {canSee(user, 'monitoring') && !(user.services_disabled || []).includes('monitoring') && <MonitoringEmailPref className="mt-6" />}
    </div>
  );
}
